import React, { useEffect, useState, useRef } from "react";
import { Button, Container, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useCargoDocumentType } from "../../../apis/cargoDocumentType/CargoDocumentTypeProvider";
import { CargoDocumentTypeApiResponse } from "../../../services/cargoDocumentTypeService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import AlertOverlay from "../../../components/Alert";

const ViewCargoDocumentTypeById: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { fetchCargoDocumentTypeById, putCargoDocumentType, excluiCargoDocumentType } = useCargoDocumentType();
    const [cargo, setCargo] = useState<CargoDocumentTypeApiResponse | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const descriptionRef = useRef<HTMLInputElement | null>(null);

    const navItems: NavBarItem[] = [
        { label: t('cargoDocumentType.labels.home'), route: "/", iconName: "house-door" },
        { label: t('cargoDocumentType.labels.list'), route: "/CargoDocumentType", iconName: "list" },
        { label: t('cargoDocumentType.labels.new'), route: "/CargoDocumentType/create", iconName: "plus-circle" },
    ];

    useEffect(() => {
        if (isEditing && descriptionRef.current) {
            descriptionRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        const loadCargo = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                const data = await fetchCargoDocumentTypeById(Number(id));
                setCargo(data);
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadCargo();
    }, [id, fetchCargoDocumentTypeById]);

    const handleEdit = async () => {
        if (!cargo) return;

        if (isEditing) {
            try {
                await putCargoDocumentType(cargo.data.id, cargo.data);

                const successMessage = getSuccessMessage("editCargoDocumentType", { id: cargo.data.id, });
                navigate(`/CargoDocumentType`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } catch (error) {
                setAlert({ message: "Erro ao atualizar os dados.", variant: "danger" });
            }
        } else {
            setIsEditing(true);
        }
    };


    
    const handleDelete = async () => {
        if (!id) return;

        try {
            const sucesso = await excluiCargoDocumentType(Number(id));

            if (sucesso) {
                const successMessage = getSuccessMessage("deleteCargoDocumentType", { id: id });
                navigate(`/CargoDocumentType`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            }
            else {
                const errorMessage = getErrorMessage("deleteDocumentTypeError", { id: id, });
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: "Erro ao excluir o registro.", variant: "danger" });
        }
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (cargo) {
            setCargo({
                ...cargo,
                data: {
                    ...cargo.data,
                    [e.target.name]: e.target.value,
                },
            });
        }
    };

    if (loading) {
        return <div className="text-center mt-4">Carregando...</div>;
    }

    if (error || !cargo) {
        return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editCargoDocumentType") : getPageName("viewCargoDocumentType")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Form.Group className="mb-2">
                    <Form.Label>{t('cargoDocumentType.fields.id')}</Form.Label>
                    <Form.Control size="sm" readOnly value={cargo.data.id} style={{ width: "20%", backgroundColor: "#f0f0f0" }} />
                </Form.Group>

                <Form.Group className="mb-2">
                    <Form.Label>{t('cargoDocumentType.fields.description')}</Form.Label>
                    <Form.Control
                        size="sm"
                        name="description"
                        value={cargo.data.description || ""}
                        onChange={handleChange}
                        readOnly={!isEditing}
                        ref={descriptionRef}
                        style={{ width: "20%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                    />
                </Form.Group>

                <Form.Group className="mb-2">
                    <Form.Label>{t('cargoDocumentType.fields.key')}</Form.Label>
                    <Form.Control size="sm" name="key" value={cargo?.data.key} style={{ width: "20%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                        onChange={handleChange}
                        readOnly={!isEditing}

                    />
                </Form.Group>
            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewCargoDocumentTypeById;
